"use client";

import { useRouter, useSearchParams } from "next/navigation";
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import useInputFocus from "@/hooks/use-input-focus/use-input-focus";
import { GetQuoteFilter } from "@/store/slices/quote/API/getQuote";
import { getQuoteData } from "@/store/slices/quote/quote.action";
import { AppDispatch, RootState } from "@/store/store";
import { Traveller } from "@/types/Traveller";
import { storageAvailable } from "@/utils/common.utils";

import {
  TravellersRequiredInformationContent,
  TravellerFormData,
  OrganiserFormData,
  ValidationState,
  TravellerValidationState,
  TravellersRequiredInformationContextType,
} from "../types/travellers-required-information.type";
import { buildRequiredInfoFieldMap } from "../utils/field-order.utils";
import { mapMagnoliaToRequiredInformationContent } from "../utils/magnolia-mapper.utils";
import {
  validateAddressLine1,
  validateAddressLine2,
  validateCity,
  validatePostalCode,
  validateIdentificationType,
  validateIdentificationNumber,
  isIdentificationOptional,
  isFormComplete as checkFormComplete,
  getValidationStatus,
} from "../utils/validation.utils";

const TravellersRequiredInformationContext =
  createContext<TravellersRequiredInformationContextType | null>(null);

export function TravellersRequiredInformationProvider({
  children,
  magnoliaData,
}: Readonly<{
  children: ReactNode;
  magnoliaData?: TravellersRequiredInformationContent;
}>) {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const searchParams = useSearchParams();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { attributes, referenceId } = quoteResponse;
  const { organiser, travellerDetails } = attributes;

  const quoteRef = searchParams.get("quoteId");
  const affiliate = searchParams.get("affiliate");

  const content = mapMagnoliaToRequiredInformationContent(magnoliaData);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (storageAvailable()) {
        sessionStorage.setItem("pageReload", "true");
      }
    };

    // Use pagehide for iOS devices, beforeunload for others
    const isOnIOS =
      typeof navigator !== "undefined" &&
      (/iPad/i.test(navigator.userAgent) ||
        /Macintosh/i.test(navigator.userAgent) ||
        /iPhone/i.test(navigator.userAgent));
    const eventName = isOnIOS ? "pagehide" : "beforeunload";

    globalThis.addEventListener(eventName, handleBeforeUnload);
    return () => {
      globalThis.removeEventListener(eventName, handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const fetchQuoteDataOnMount = async () => {
      let getQuoteFilter: GetQuoteFilter = {
        affiliate: null,
        quoteId: null,
      };

      if (!affiliate && !quoteRef) {
        getQuoteFilter.affiliate = "quoteInit";
      } else {
        getQuoteFilter = {
          affiliate,
          quoteId: quoteRef,
        };
      }

      const shouldFetchQuote =
        (storageAvailable() && sessionStorage.getItem("pageReload")) ||
        !sessionStorage.getItem("initialLoad");

      if (shouldFetchQuote && !referenceId) {
        sessionStorage.setItem("initialLoad", "true");
        sessionStorage.removeItem("pageReload");
        await dispatch(getQuoteData({ getQuoteFilter, dispatch, router }));
      }
    };

    fetchQuoteDataOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const organiserAge = useMemo(() => {
    if (organiser?.dateOfBirth) {
      const dob = new Date(organiser.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < dob.getDate())
      ) {
        age--;
      }
      return age;
    }
    return undefined;
  }, [organiser?.dateOfBirth]);

  const [organiserData, setOrganiserData] = useState<OrganiserFormData>({
    firstName: organiser?.firstName || "",
    lastName: organiser?.lastName || "",
    age: organiserAge,
    addressLine1: organiser?.firstLineOfAddress || "",
    addressLine2: organiser?.secondLineOfAddress || "",
    city: organiser?.city || "",
    postalCode: organiser?.postCode || "",
    identificationType: organiser?.identificationType || "",
    identificationNumber: organiser?.identificationNumber || "",
  });

  // Must be declared before travellersData to avoid "Cannot access before initialization" error
  const [organiserTravellerEncryptedId, setOrganiserTravellerEncryptedId] =
    useState<string | null>(() => {
      if (organiser?.travelling) {
        const organiserTraveller = travellerDetails?.traveller?.find(
          (t: Traveller) => t.sequence === 1
        );
        return organiserTraveller?.encryptedId || null;
      }
      return null;
    });

  const [travellersData, setTravellersData] = useState<TravellerFormData[]>(
    () => {
      const allTravellers = travellerDetails?.traveller || [];

      // Filter out organiser if they're travelling (sequence === 1)
      const displayTravellers = organiser?.travelling
        ? allTravellers.filter((t: Traveller) => t.sequence !== 1)
        : allTravellers;

      return displayTravellers.map((t: Traveller) => ({
        encryptedId: t.encryptedId || "",
        firstName: t.firstName || "",
        lastName: t.lastName || "",
        age: t.age || 0,
        identificationType: t.identificationType || "",
        identificationNumber: t.identificationNumber || "",
      }));
    }
  );

  useEffect(() => {
    if (organiser) {
      setOrganiserData({
        firstName: organiser.firstName || "",
        lastName: organiser.lastName || "",
        age: organiserAge,
        addressLine1: organiser.firstLineOfAddress || "",
        addressLine2: organiser.secondLineOfAddress || "",
        city: organiser.city || "",
        postalCode: organiser.postCode || "",
        identificationType: organiser.identificationType || "",
        identificationNumber: organiser.identificationNumber || "",
      });

      setOrganiserPreFilled({
        addressLine1: !!organiser.firstLineOfAddress?.trim(),
        addressLine2: !!organiser.secondLineOfAddress?.trim(),
        city: !!organiser.city?.trim(),
        postalCode: !!organiser.postCode?.trim(),
        identificationType: !!organiser.identificationType?.trim(),
        identificationNumber: !!organiser.identificationNumber?.trim(),
      });
    }
  }, [organiser, organiserAge]);

  useEffect(() => {
    if (travellerDetails?.traveller) {
      const allTravellers = travellerDetails.traveller;

      const organiserTraveller = allTravellers.find(
        (t: Traveller) => t.sequence === 1
      );
      if (organiser?.travelling && organiserTraveller) {
        setOrganiserTravellerEncryptedId(
          organiserTraveller.encryptedId || null
        );
      } else {
        setOrganiserTravellerEncryptedId(null);
      }

      // Filter out organiser if they're travelling (sequence === 1)
      const displayTravellers = organiser?.travelling
        ? allTravellers.filter((t: Traveller) => t.sequence !== 1)
        : allTravellers;

      setTravellersData(
        displayTravellers.map((t: Traveller) => ({
          encryptedId: t.encryptedId || "",
          firstName: t.firstName || "",
          lastName: t.lastName || "",
          age: t.age || 0,
          identificationType: t.identificationType || "",
          identificationNumber: t.identificationNumber || "",
        }))
      );

      const preFilled: Record<string, Record<string, boolean>> = {};
      displayTravellers.forEach((t: Traveller) => {
        if (t.encryptedId) {
          preFilled[t.encryptedId] = {
            identificationType: !!t.identificationType?.trim(),
            identificationNumber: !!t.identificationNumber?.trim(),
          };
        }
      });
      setTravellersPreFilled(preFilled);
    }
  }, [travellerDetails, organiser?.travelling]);

  const [organiserTouched, setOrganiserTouched] = useState<
    Record<string, boolean>
  >({});
  const [travellersTouched, setTravellersTouched] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const [organiserPreFilled, setOrganiserPreFilled] = useState<
    Record<string, boolean>
  >({});
  const [travellersPreFilled, setTravellersPreFilled] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const [showErrors, setShowErrors] = useState<boolean>(false);

  const initialFocusState = useMemo(() => {
    const organiserKeys = [
      "organiser-addressLine1",
      "organiser-addressLine2",
      "organiser-city",
      "organiser-postalCode",
      "organiser-identificationType",
      "organiser-identificationNumber",
    ];

    const travellerKeys = travellersData.flatMap((t) => [
      `traveller-${t.encryptedId}-identificationType`,
      `traveller-${t.encryptedId}-identificationNumber`,
    ]);

    const allKeys = [...organiserKeys, ...travellerKeys];

    return allKeys.reduce(
      (acc, key) => {
        acc[key] = false;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [travellersData]);

  const { getWarningStatus, setFocusedInputs, setAllFocused } =
    useInputFocus(initialFocusState);

  useEffect(() => {
    if (showErrors) setAllFocused();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors]);

  const getFocusedFields = useCallback(
    (fieldKey: string) => {
      const fieldMap = buildRequiredInfoFieldMap(travellersData);
      return fieldMap[fieldKey] ?? [];
    },
    [travellersData]
  );

  const organiserErrors = useMemo(
    () => ({
      addressLine1: validateAddressLine1(organiserData.addressLine1),
      addressLine2: validateAddressLine2(organiserData.addressLine2 || ""),
      city: validateCity(organiserData.city),
      postalCode: validatePostalCode(organiserData.postalCode),
      identificationType: validateIdentificationType(
        organiserData.identificationType
      ),
      identificationNumber: validateIdentificationNumber(
        organiserData.identificationType,
        organiserData.identificationNumber,
        false
      ),
    }),
    [organiserData]
  );

  const organiserValidation: ValidationState = useMemo(
    () => ({
      addressLine1: getValidationStatus(
        organiserData.addressLine1,
        organiserErrors.addressLine1,
        organiserTouched.addressLine1 || false,
        true,
        organiserPreFilled.addressLine1 || false,
        getWarningStatus("organiser-addressLine1") === "warning",
        showErrors
      ),
      addressLine2: getValidationStatus(
        organiserData.addressLine2,
        organiserErrors.addressLine2,
        organiserTouched.addressLine2 || false,
        false,
        organiserPreFilled.addressLine2 || false,
        getWarningStatus("organiser-addressLine2") === "warning",
        showErrors
      ),
      city: getValidationStatus(
        organiserData.city,
        organiserErrors.city,
        organiserTouched.city || false,
        true,
        organiserPreFilled.city || false,
        getWarningStatus("organiser-city") === "warning",
        showErrors
      ),
      postalCode: getValidationStatus(
        organiserData.postalCode,
        organiserErrors.postalCode,
        organiserTouched.postalCode || false,
        true,
        organiserPreFilled.postalCode || false,
        getWarningStatus("organiser-postalCode") === "warning",
        showErrors
      ),
      identificationType: getValidationStatus(
        organiserData.identificationType,
        organiserErrors.identificationType,
        organiserTouched.identificationType || false,
        true,
        organiserPreFilled.identificationType || false,
        getWarningStatus("organiser-identificationType") === "warning",
        showErrors
      ),
      identificationNumber: getValidationStatus(
        organiserData.identificationNumber,
        organiserErrors.identificationNumber,
        organiserTouched.identificationNumber || false,
        true,
        organiserPreFilled.identificationNumber || false,
        getWarningStatus("organiser-identificationNumber") === "warning",
        showErrors
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      organiserErrors,
      organiserTouched,
      organiserPreFilled,
      getWarningStatus,
      showErrors,
    ]
  );

  const travellersErrors = useMemo(() => {
    const errors: Record<
      string,
      { identificationType: string | null; identificationNumber: string | null }
    > = {};

    travellersData.forEach((traveller) => {
      const optional = isIdentificationOptional(traveller.age);

      errors[traveller.encryptedId] = {
        identificationType: validateIdentificationType(
          traveller.identificationType
        ),
        identificationNumber: validateIdentificationNumber(
          traveller.identificationType,
          traveller.identificationNumber,
          optional
        ),
      };
    });

    return errors;
  }, [travellersData]);

  const travellersValidation = useMemo(() => {
    const validation: Record<string, TravellerValidationState> = {};

    travellersData.forEach((traveller) => {
      const optional = isIdentificationOptional(traveller.age);
      const touched = travellersTouched[traveller.encryptedId] || {};
      const preFilled = travellersPreFilled[traveller.encryptedId] || {};

      validation[traveller.encryptedId] = {
        identificationType: getValidationStatus(
          traveller.identificationType,
          travellersErrors[traveller.encryptedId]?.identificationType,
          touched.identificationType || false,
          !optional,
          preFilled.identificationType || false,
          getWarningStatus(
            `traveller-${traveller.encryptedId}-identificationType`
          ) === "warning",
          showErrors
        ),
        identificationNumber: getValidationStatus(
          traveller.identificationNumber,
          travellersErrors[traveller.encryptedId]?.identificationNumber,
          touched.identificationNumber || false,
          !optional,
          preFilled.identificationNumber || false,
          getWarningStatus(
            `traveller-${traveller.encryptedId}-identificationNumber`
          ) === "warning",
          showErrors
        ),
      };
    });

    return validation;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    travellersErrors,
    travellersTouched,
    travellersPreFilled,
    getWarningStatus,
    showErrors,
  ]);

  const isFormComplete = useMemo(() => {
    return checkFormComplete({
      organiser: {
        addressLine1: organiserData.addressLine1,
        addressLine2: organiserData.addressLine2,
        city: organiserData.city,
        postalCode: organiserData.postalCode,
        identificationType: organiserData.identificationType,
        identificationNumber: organiserData.identificationNumber,
      },
      travellers: travellersData.map((t) => ({
        age: t.age,
        identificationType: t.identificationType,
        identificationNumber: t.identificationNumber,
      })),
    });
  }, [organiserData, travellersData]);

  const selectedStateCmsData = useMemo(() => {
    return isFormComplete
      ? content?.descriptionSection?.filledState || {}
      : content?.descriptionSection?.emptyState || {};
  }, [isFormComplete, content]);

  const identificationTypes = useMemo(
    () => [
      {
        id: "DNI",
        label:
          content?.organiserSection?.identificationSection?.typeOptions
            ?.dniLabel ?? "",
      },
      {
        id: "NIE",
        label:
          content?.organiserSection?.identificationSection?.typeOptions
            ?.nieLabel ?? "",
      },
      {
        id: "PASSPORT",
        label:
          content?.organiserSection?.identificationSection?.typeOptions
            ?.passportLabel ?? "",
      },
    ],
    [content]
  );

  const updateOrganiserField = useCallback(
    (field: keyof OrganiserFormData, value: string) => {
      setOrganiserData((prev) => ({
        ...prev,
        [field]: value,
      }));

      setOrganiserPreFilled((prev) => ({
        ...prev,
        [field]: false,
      }));
    },
    []
  );

  const updateTravellerField = useCallback(
    (encryptedId: string, field: keyof TravellerFormData, value: string) => {
      setTravellersData((prev) =>
        prev.map((t) =>
          t.encryptedId === encryptedId ? { ...t, [field]: value } : t
        )
      );

      setTravellersPreFilled((prev) => ({
        ...prev,
        [encryptedId]: {
          ...prev[encryptedId],
          [field]: false,
        },
      }));
    },
    []
  );

  const setFieldTouched = useCallback(
    (section: string, field: string) => {
      if (section === "organiser") {
        setOrganiserTouched((prev) => ({ ...prev, [field]: true }));
      } else {
        // Section is encryptedId for travellers
        setTravellersTouched((prev) => ({
          ...prev,
          [section]: { ...prev[section], [field]: true },
        }));
      }

      // Sequential validation: mark all previous fields as focused
      const fieldKey =
        section === "organiser"
          ? `organiser-${field}`
          : `traveller-${section}-${field}`;
      const previousFields = getFocusedFields(fieldKey);
      setFocusedInputs(previousFields);
    },
    [getFocusedFields, setFocusedInputs]
  );

  const value: TravellersRequiredInformationContextType = useMemo(
    () => ({
      magnoliaData: content,
      selectedStateCmsData,
      identificationTypes,
      organiserData,
      travellersData,
      organiserTravelling: organiser?.travelling || false,
      organiserTravellerEncryptedId,
      organiserValidation,
      travellersValidation,
      organiserErrors,
      travellersErrors,
      organiserTouched,
      travellersTouched,
      isFormComplete,
      showErrors,
      setShowErrors,
      updateOrganiserField,
      updateTravellerField,
      setFieldTouched,
    }),
    [
      content,
      selectedStateCmsData,
      identificationTypes,
      organiserData,
      travellersData,
      organiser?.travelling,
      organiserTravellerEncryptedId,
      organiserValidation,
      travellersValidation,
      organiserErrors,
      travellersErrors,
      organiserTouched,
      travellersTouched,
      isFormComplete,
      showErrors,
      setShowErrors,
      updateOrganiserField,
      updateTravellerField,
      setFieldTouched,
    ]
  );

  return (
    <TravellersRequiredInformationContext.Provider value={value}>
      {children}
    </TravellersRequiredInformationContext.Provider>
  );
}

export function useTravellersRequiredInformation() {
  const context = useContext(TravellersRequiredInformationContext);
  if (!context) {
    throw new Error(
      "useTravellersRequiredInformation must be used within TravellersRequiredInformationProvider"
    );
  }
  return context;
}
